import type { JSX } from "preact";
import { useEffect } from "preact/hooks";
import {
  archiveMessages,
  clearSearch,
  closeComposerSafe,
  closeEmlPreview,
  closeReader,
  deleteMessages,
  dismissToast,
  openCompose,
  openComposeFor,
  openNeighbor,
  printMessage,
  selectedRefs,
  selectMailbox,
  specialPath,
  spamMessages,
  toggleFlagged,
  toggleSeen,
  toggleSelect,
} from "../actions";
import {
  composer,
  currentMailbox,
  emlPreview,
  emlPreviewError,
  emlPreviewLoading,
  fullMessage,
  isSearching,
  movePicker,
  selectedMailbox,
  selectedUid,
  selectedUids,
  showCommandPalette,
  showShortcuts,
  toasts,
} from "../state";
import { t } from "../i18n";
import { Dialog, DialogHeader } from "./kit";

function isEditing(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.getAttribute("contenteditable") === "true" || (el as HTMLElement).isContentEditable) return true;
  return false;
}

/**
 * Escape-key priority chain: closes the topmost overlay first. Exported
 * (rather than inlined in the switch) so the priority order is unit-testable
 * without a DOM/event-dispatch harness — signals in, action out.
 */
export function handleEscape(): void {
  if (emlPreview.value !== null || emlPreviewLoading.value || emlPreviewError.value !== null) {
    closeEmlPreview();
  } else if (composer.value !== null) {
    closeComposerSafe();
  } else if (isSearching.value) {
    clearSearch();
  } else if (showShortcuts.value) {
    showShortcuts.value = false;
  } else if (selectedUids.value.size > 0) {
    selectedUids.value = new Set();
  } else {
    selectedUid.value = null;
    fullMessage.value = null;
  }
}

/** Targets for message actions: the checked messages, else the open one. */
function targets(): Array<{ uid: number; mailbox: string }> {
  if (selectedUids.value.size > 0) return selectedRefs();
  const m = fullMessage.value;
  if (m) return [{ uid: m.uid, mailbox: m.mailbox }];
  const uid = selectedUid.value;
  return uid != null ? [{ uid, mailbox: selectedMailbox.value ?? currentMailbox.value }] : [];
}

const GO_TO: Record<string, "inbox" | "sent" | "drafts" | "archive" | "trash" | "junk"> = {
  i: "inbox",
  s: "sent",
  d: "drafts",
  a: "archive",
  t: "trash",
  j: "junk",
};

export function KeyboardHandler(): null {
  useEffect(() => {
    let pendingG = 0;

    function handleKeyDown(e: KeyboardEvent): void {
      // Command palette works everywhere, even while typing.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        showCommandPalette.value = !showCommandPalette.value;
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditing()) return;
      // Overlays with their own focus handle their own keys.
      if (document.querySelector("[aria-modal='true']") && e.key !== "Escape") return;

      const key = e.key;

      // Two-key "go to" sequences: g then i/s/d/a/t/j.
      if (pendingG && Date.now() - pendingG < 1200) {
        pendingG = 0;
        const use = GO_TO[key];
        if (use) {
          e.preventDefault();
          void selectMailbox(specialPath(use) ?? (use === "inbox" ? "INBOX" : currentMailbox.value));
        }
        return;
      }

      switch (key) {
        case "j":
        case "ArrowDown":
          if (key === "ArrowDown" && !fullMessage.value) return;
          e.preventDefault();
          openNeighbor(1);
          break;
        case "k":
        case "ArrowUp":
          if (key === "ArrowUp" && !fullMessage.value) return;
          e.preventDefault();
          openNeighbor(-1);
          break;
        case "g":
          pendingG = Date.now();
          break;
        case "r":
          if (fullMessage.value) {
            e.preventDefault();
            openComposeFor("reply");
          }
          break;
        case "a":
          if (fullMessage.value) {
            e.preventDefault();
            openComposeFor("replyAll");
          }
          break;
        case "f":
          if (fullMessage.value) {
            e.preventDefault();
            openComposeFor("forward");
          }
          break;
        case "e":
          void archiveMessages(targets());
          break;
        case "#":
        case "Delete":
          void deleteMessages(targets());
          break;
        case "!":
          void spamMessages(targets());
          break;
        case "v": {
          const list = targets();
          if (list.length) movePicker.value = { uids: list.map((r) => r.uid), mailbox: list[0]!.mailbox };
          break;
        }
        case "u": {
          const m = fullMessage.value;
          if (m) void toggleSeen(m.uid, m.mailbox);
          break;
        }
        case "s": {
          const m = fullMessage.value;
          if (m) void toggleFlagged(m.uid, m.mailbox);
          break;
        }
        case "x": {
          const uid = selectedUid.value;
          if (uid != null) toggleSelect(uid);
          break;
        }
        case "p":
          if (fullMessage.value) {
            e.preventDefault();
            printMessage();
          }
          break;
        case "z": {
          // Undo the most recent undoable action still on screen.
          const last = [...toasts.value].reverse().find((x) => x.action);
          if (last?.action) {
            last.action.run();
            dismissToast(last.id);
          }
          break;
        }
        case "c":
          e.preventDefault();
          openCompose();
          break;
        case "/": {
          e.preventDefault();
          document.querySelector<HTMLInputElement>("[data-search-input]")?.focus();
          break;
        }
        case "Escape":
          handleEscape();
          if (!fullMessage.value) closeReader();
          break;
        case "?":
          showShortcuts.value = !showShortcuts.value;
          break;
        default:
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return null;
}

type ShortcutGroup = { title: string; items: Array<{ keys: string[]; label: string }> };

function shortcutGroups(): ShortcutGroup[] {
  return [
    {
      title: t("shortcuts.navigation"),
      items: [
        { keys: ["J"], label: t("shortcuts.next") },
        { keys: ["K"], label: t("shortcuts.prev") },
        { keys: ["G", "I"], label: t("shortcuts.goInbox") },
        { keys: ["G", "S"], label: t("shortcuts.goSent") },
        { keys: ["G", "D"], label: t("shortcuts.goDrafts") },
        { keys: ["G", "A"], label: t("shortcuts.goArchive") },
        { keys: ["/"], label: t("shortcuts.search") },
        { keys: ["Ctrl", "K"], label: t("shortcuts.palette") },
        { keys: ["Esc"], label: t("shortcuts.escape") },
      ],
    },
    {
      title: t("shortcuts.actions"),
      items: [
        { keys: ["E"], label: t("action.archive") },
        { keys: ["#"], label: t("action.delete") },
        { keys: ["!"], label: t("action.spam") },
        { keys: ["V"], label: t("action.move") },
        { keys: ["U"], label: t("shortcuts.toggleRead") },
        { keys: ["S"], label: t("shortcuts.toggleStar") },
        { keys: ["X"], label: t("shortcuts.select") },
        { keys: ["Z"], label: t("shortcuts.undo") },
        { keys: ["P"], label: t("reader.print") },
      ],
    },
    {
      title: t("shortcuts.writing"),
      items: [
        { keys: ["C"], label: t("compose.new") },
        { keys: ["R"], label: t("action.reply") },
        { keys: ["A"], label: t("action.replyAll") },
        { keys: ["F"], label: t("action.forward") },
        { keys: ["Ctrl", "Enter"], label: t("compose.send") },
        { keys: ["Ctrl", "S"], label: t("shortcuts.saveDraft") },
      ],
    },
  ];
}

export function ShortcutsModal(): JSX.Element | null {
  if (!showShortcuts.value) return null;
  const close = (): void => {
    showShortcuts.value = false;
  };
  return (
    <Dialog onClose={close} label={t("shortcuts.title")} class="max-w-3xl">
      <DialogHeader title={t("shortcuts.title")} onClose={close} />
      <div class="grid max-h-[70vh] gap-x-8 gap-y-6 overflow-y-auto p-5 sm:grid-cols-3">
        {shortcutGroups().map((g) => (
          <section key={g.title}>
            <h3 class="mb-2.5 text-xs font-medium text-fg-3">{g.title}</h3>
            <ul class="space-y-2">
              {g.items.map((it) => (
                <li key={it.label + it.keys.join()} class="flex items-center justify-between gap-3 text-[13px] text-fg-2">
                  <span>{it.label}</span>
                  <span class="flex shrink-0 gap-1">
                    {it.keys.map((k) => (
                      <kbd key={k} class="kbd">
                        {k}
                      </kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
